import { ConditionalLogicRule } from '../types/questionnaire.types';

/**
 * Reserved pseudo-question used by conditional rules that depend on the
 * questionnaire's referral (from the ?referral=<hash> link) rather than on an
 * answer. Its value is the referral id, or '' when there is no referral, so every
 * existing operator (equals / in / is_empty / …) works unchanged. Referral
 * conditions carry their target in `referral_id` rather than `value`, which is what
 * lets config sync remap the id between environments.
 */
export const REFERRAL_CONDITION_KEY = '__referral__';

/**
 * Reserved pseudo-question for conditions that depend on whether the
 * questionnaire is tied to a lead rather than on an answer. Its synthetic value
 * is 'yes' when a lead exists and '' otherwise, so `equals 'yes'` shows a field
 * only when there is a lead (hide-if-no-lead) and `not_equals 'yes'` shows it
 * only when there is none (hide-if-lead) — using the existing operators
 * unchanged.
 */
export const LEAD_CONDITION_KEY = '__lead__';

/** The synthetic value a __lead__ condition compares against. */
export const LEAD_PRESENT_VALUE = 'yes';

/** The value a condition compares against: referral ids for referral conditions. */
const conditionTarget = (cond: { question?: string; value?: any; referral_id?: number | number[]; [key: string]: any }): any =>
  cond.question === REFERRAL_CONDITION_KEY && cond.referral_id !== undefined
    ? cond.referral_id
    : cond.value;

/**
 * Evaluates if a field should be visible based on conditional logic rules and current form values
 * @param fieldName - The field to check visibility for
 * @param formValues - Current form values
 * @param conditionalLogic - Array of questionnaire-level conditional logic rules (legacy)
 * @param fieldLevelRule - Field-level conditional logic rule (new)
 * @returns true if field should be visible, false if hidden
 */
export const evaluateFieldVisibility = (
  fieldName: string,
  formValues: Record<string, any>,
  conditionalLogic?: ConditionalLogicRule[],
  fieldLevelRule?: ConditionalLogicRule
): boolean => {

  // If field has a field-level rule, use that (takes precedence)
  if (fieldLevelRule) {
    // Prefer `conditions` (multi-condition AND), fall back to single `showIf` for legacy rules.
    const conditionList =
      fieldLevelRule.conditions && fieldLevelRule.conditions.length > 0
        ? fieldLevelRule.conditions
        : [fieldLevelRule.showIf];


    // All conditions must pass (AND).
    const result = conditionList.every((cond) => {
      const { question, operator = 'equals' } = cond;
      const currentValue = formValues[question];
      return evaluateCondition(currentValue, conditionTarget(cond), operator);
    });

    return result;
  }

  // Fall back to questionnaire-level rules (legacy support)
  if (!conditionalLogic || conditionalLogic.length === 0) {
    return true;
  }

  // Find all rules that apply to this field
  const rulesForField = conditionalLogic.filter((rule) => rule.field === fieldName);

  // If no rules for this field, it's visible
  if (rulesForField.length === 0) {
    return true;
  }

  // Evaluate rules - if ANY rule matches, field is visible (OR logic)
  for (const rule of rulesForField) {
    const { question, operator = 'equals' } = rule.showIf;
    const currentValue = formValues[question];

    if (evaluateCondition(currentValue, conditionTarget(rule.showIf), operator)) {
      return true;
    }
  }

  // No rules matched = field is hidden
  return false;
};

/**
 * Evaluates a single condition
 * @param currentValue - Current value from form
 * @param targetValue - Target value to compare against
 * @param operator - Comparison operator
 * @returns true if condition matches
 */
export const evaluateCondition = (
  currentValue: any,
  targetValue: any,
  operator: string = 'equals'
): boolean => {

  // Scalar match with numeric coercion (so "5" matches 5).
  const scalarMatch = (a: any, b: any): boolean => {
    if (a === b) return true;
    const aNum = Number(a);
    const bNum = Number(b);
    return !isNaN(aNum) && !isNaN(bNum) && aNum === bNum;
  };

  switch (operator) {
    case 'equals': {
      // When currentValue is an array (e.g., the dependency is a multi-select /
      // checkboxes field), interpret `equals X` as "X is among the selected values".
      if (Array.isArray(currentValue)) {
        const result = currentValue.some((v) => scalarMatch(v, targetValue));
        return result;
      }
      const equalsResult = scalarMatch(currentValue, targetValue);
      return equalsResult;
    }

    case 'not_equals': {
      // When currentValue is an array, interpret `not_equals X` as "X is NOT among
      // the selected values" — i.e. the rule fails if the user selected X.
      if (Array.isArray(currentValue)) {
        const result = !currentValue.some((v) => scalarMatch(v, targetValue));
        return result;
      }
      const notEqualsResult = !scalarMatch(currentValue, targetValue);
      return notEqualsResult;
    }

    case 'contains':
      // For arrays, check if targetValue is in the array
      if (Array.isArray(currentValue)) {
        const arrayContainsResult = currentValue.includes(targetValue);
        return arrayContainsResult;
      }
      // For strings, check if string contains substring
      if (typeof currentValue === 'string') {
        const stringContainsResult = currentValue.includes(targetValue);
        return stringContainsResult;
      }
      return false;

    case 'in': {
      // Normalize targetValue to an array. Supports:
      //   - array form (new): ['yes', 'maybe']
      //   - comma-separated string (legacy): 'yes,maybe'
      //   - single scalar (legacy): 'yes'
      const targets: any[] = Array.isArray(targetValue)
        ? targetValue
        : typeof targetValue === 'string'
          ? targetValue.split(',').map((s) => s.trim()).filter((s) => s.length > 0)
          : targetValue !== undefined && targetValue !== null
            ? [targetValue]
            : [];

      const matches = (a: any, b: any): boolean => {
        if (a === b) return true;
        const aNum = Number(a);
        const bNum = Number(b);
        return !isNaN(aNum) && !isNaN(bNum) && aNum === bNum;
      };

      // currentValue may itself be an array (e.g., the dependency field is
      // a checkboxes/multiselect). Treat the rule as "any selected value is in targets".
      const currentValues: any[] = Array.isArray(currentValue) ? currentValue : [currentValue];
      const inResult = currentValues.some((cv) => targets.some((t) => matches(cv, t)));
      return inResult;
    }

    case 'is_empty':
      const isEmptyResult = !currentValue || (Array.isArray(currentValue) && currentValue.length === 0);
      return isEmptyResult;

    case 'is_not_empty':
      const isNotEmptyResult = currentValue && !(Array.isArray(currentValue) && currentValue.length === 0);
      return isNotEmptyResult;

    case 'greater_than':
      const currentNum = Number(currentValue);
      const targetNum = Number(targetValue);
      const gtResult = currentNum > targetNum;
      return gtResult;

    case 'less_than':
      const currentNum2 = Number(currentValue);
      const targetNum2 = Number(targetValue);
      const ltResult = currentNum2 < targetNum2;
      return ltResult;

    case 'greater_or_equal':
      const currentNum3 = Number(currentValue);
      const targetNum3 = Number(targetValue);
      const geResult = currentNum3 >= targetNum3;
      return geResult;

    case 'less_or_equal':
      const currentNum4 = Number(currentValue);
      const targetNum4 = Number(targetValue);
      const leResult = currentNum4 <= targetNum4;
      return leResult;

    default:
      return true; // Unknown operator = visible by default
  }
};

/**
 * Gets all visible fields for a section based on form values
 * @param fields - Array of fields to check
 * @param formValues - Current form values
 * @param conditionalLogic - Conditional logic rules
 * @returns Array of visible fields
 */
export const getVisibleFields = (
  fields: any[],
  formValues: Record<string, any>,
  conditionalLogic?: ConditionalLogicRule[]
) => {
  return fields.filter((field) => evaluateFieldVisibility(field.name, formValues, conditionalLogic));
};

/**
 * Gets field names to validate based on visibility
 * Only visible fields should be validated
 * @param fields - Array of fields
 * @param formValues - Current form values
 * @param conditionalLogic - Conditional logic rules
 * @returns Array of field names that should be validated
 */
export const getFieldsToValidate = (
  fields: any[],
  formValues: Record<string, any>,
  conditionalLogic?: ConditionalLogicRule[]
): string[] => {
  return getVisibleFields(fields, formValues, conditionalLogic).map((field) => field.name);
};
